import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import styled from '@emotion/styled';
import { checkGrammar, clearResult, queryWord, clearWordResult } from '../store/grammarSlice';
import { favoriteWord, unfavoriteWord, setShowLoginModal, getFavorites } from '../store/authSlice';
import LoginModal from './LoginModal';
import UserInfo from './UserInfo';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h1`
  text-align: center;
  color: #ffffff;
  margin-bottom: 30px;
  font-size: 2.5rem;
  font-weight: 600;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LogoIcon = styled.div`
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
`;

const LogoSVG = () => (
  <svg viewBox="0 0 44 44" width="28" height="28" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Chat bubble with language symbol */}
    <path d="M6 12C6 8.686 8.686 6 12 6h20c3.314 0 6 2.686 6 6v14c0 3.314-2.686 6-6 6h-4l-6 5-2-5H12c-3.314 0-6-2.686-6-6V12z" 
      fill="white" opacity="0.95"/>
    <text x="22" y="27" text-anchor="middle" font-size="16" font-weight="700" fill="#667eea" font-family="Arial">L</text>
  </svg>
);

const BrandName = styled.span`
  font-size: 1.6rem;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.5px;
`;

const LogoTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Tagline = styled.span`
  font-size: 0.8rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.55);
  letter-spacing: 0.5px;
  padding-left: 2px;
`;

const InputSection = styled.div`
  margin-bottom: 30px;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 150px;
  padding: 15px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  font-size: 16px;
  font-family: inherit;
  resize: vertical;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 15px;
  align-items: center;
`;

const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
`;

const SwitchLabel = styled.span`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
`;

const Switch = styled.label`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
`;

const SwitchInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background-color: #28a745;
  }

  &:checked + span:before {
    transform: translateX(26px);
  }

  &:focus + span {
    box-shadow: 0 0 1px #28a745;
  }
`;

const Slider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #6c757d;
  transition: .4s;
  border-radius: 24px;

  &:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: .4s;
    border-radius: 50%;
  }
`;

const PrimaryButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
  }
  
  &:disabled {
    background: #555;
    color: #888;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const SecondaryButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.5);
    color: #ffffff;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid #ffffff;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s ease-in-out infinite;
  margin-right: 8px;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ResultSection = styled.div`
  margin-top: 30px;
`;

const ResultCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  border-left: 4px solid #667eea;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const ResultHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ResultTitle = styled.h3`
  margin: 0;
  color: #ffffff;
  font-size: 1.2rem;
  font-weight: 600;
`;

const OriginalText = styled.p`
  background: #fff;
  padding: 15px;
  border-radius: 6px;
  border: 1px solid #dee2e6;
  margin: 0;
  line-height: 1.6;
  color: #495057;
`;

const CorrectedText = styled.p`
  background: rgba(102, 126, 234, 0.1);
  padding: 15px;
  border-radius: 8px;
  border: 1px solid rgba(102, 126, 234, 0.3);
  margin: 0;
  line-height: 1.6;
  color: #ffffff;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const TranslationText = styled.p`
  background: rgba(118, 75, 162, 0.1);
  padding: 15px;
  border-radius: 8px;
  border: 1px solid rgba(118, 75, 162, 0.3);
  margin: 0;
  line-height: 1.6;
  color: #ffffff;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const ActionButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.7);
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  backdrop-filter: blur(10px);
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
    color: #ffffff;
  }
  
  &.copied {
    color: #4ade80;
    background-color: rgba(74, 222, 128, 0.2);
  }
  
  &.email {
    color: #60a5fa;
    
    &:hover {
      color: #3b82f6;
    }
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const CopyButton = styled(ActionButton)``;
const EmailButton = styled(ActionButton)``;

const CopyIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
  </svg>
);

const EmailIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
  </svg>
);

// 新增：单词查询结果相关的样式组件
const WordResultSection = styled.div`
  margin-top: 30px;
`;

const WordResultCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  border-left: 4px solid #667eea;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const WordTitle = styled.h3`
  margin: 0 0 15px 0;
  color: #ffffff;
  font-size: 1.5rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PhoneticText = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-style: italic;
  margin: 10px 0;
  font-size: 1.1rem;
`;

const DefinitionsList = styled.ul`
  margin: 15px 0;
  padding-left: 20px;
`;

const DefinitionItem = styled.li`
  margin: 8px 0;
  line-height: 1.5;
`;

// 新增：Skills 内容样式组件
const SkillsContentSection = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
`;

const SkillsTitle = styled.h4`
  margin: 0 0 10px 0;
  color: #667eea;
  font-size: 1rem;
  font-weight: 600;
`;

const SkillsContent = styled.div`
  background: rgba(102, 126, 234, 0.1);
  padding: 15px;
  border-radius: 8px;
  border: 1px solid rgba(102, 126, 234, 0.3);
  color: #ffffff;
  line-height: 1.6;
`;

const SkillItem = styled.div`
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
`;

const SkillSentence = styled.div`
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 8px;
  font-size: 1.05rem;
`;

const SkillTranslation = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.95rem;
`;

const PartOfSpeech = styled.span`
  font-weight: 600;
  color: #667eea;
  margin-right: 8px;
`;

const PlayButton = styled.button`
  background: #667eea;
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 10px rgba(102, 126, 234, 0.3);
  
  &:hover {
    background: #5a6fd8;
    transform: scale(1.1);
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.5);
  }
  
  &:disabled {
    background: #555;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const PlayIcon = ({ playing }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    {playing ? (
      <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
    ) : (
      <path d="M8 5v14l11-7z"/>
    )}
  </svg>
);

// 新增：收藏按钮样式
const FavoriteButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  margin-left: 10px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }
  
  &.favorited {
    color: #ef4444;
    background: rgba(239, 68, 68, 0.1);
    
    &:hover {
      background: rgba(239, 68, 68, 0.2);
    }
  }
  
  &:disabled {
    cursor: not-allowed;
    transform: none;
    opacity: 0.5;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const FavoriteIcon = ({ favorited }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill={favorited ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
  </svg>
);

// 新增：上下文菜单样式
const ContextMenu = styled.div`
  position: fixed;
  background: rgba(30, 30, 30, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  padding: 4px 0;
  z-index: 1000;
  backdrop-filter: blur(20px);
`;

const ContextMenuItem = styled.div`
  padding: 10px 16px;
  cursor: pointer;
  font-size: 14px;
  color: #ffffff;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(102, 126, 234, 0.3);
  }
`;

const GrammarChecker = () => {
  const [text, setText] = useState('');
  const [copiedStates, setCopiedStates] = useState({
    corrected: false,
    translation: false,
    betterExpression: false
  });
  const [autoMode, setAutoMode] = useState(true); // true: 自动查询, false: 手动查询
  const [isPlaying, setIsPlaying] = useState(false); // 新增：音频播放状态
  const [contextMenu, setContextMenu] = useState({
    show: false,
    x: 0,
    y: 0,
    selectedText: ''
  }); // 新增：上下文菜单状态
  const [hasClipboardContent, setHasClipboardContent] = useState(false); // 新增：剪贴板内容状态
  const [clipboardPermission, setClipboardPermission] = useState('unknown'); // 新增：剪贴板权限状态
  
  const audioRef = useRef(null); // 新增：音频引用
  const timeoutRef = useRef(null);
  
  const dispatch = useDispatch();
  const { result, wordResult, loading, error, isChinese } = useSelector(state => state.grammar);
  const { isAuthenticated, user, favoriteWords, favorites } = useSelector(state => state.auth);

  // 解析技能内容的函数
  const parseSkillContent = (content) => {
    if (!content) return [];

    // 按空行分割内容块
    const blocks = content.split('\n\n').filter(block => block.trim());

    return blocks.map(block => {
      // 查找英文句子（用 ** 包围）
      const sentenceMatch = block.match(/\*\*(.+?)\*\*/);
      const sentence = sentenceMatch ? sentenceMatch[1] : block;

      // 获取翻译（去掉英文句子部分）
      const translation = block.replace(/\*\*.+?\*\*/, '').trim();

      return {
        sentence: sentence,
        translation: translation
      };
    });
  };

  // 如果用户已登录但收藏列表为空，自动获取收藏列表
  useEffect(() => {
    if (isAuthenticated && favorites.length === 0) {
      dispatch(getFavorites()).unwrap().catch(err => {
        console.log('获取收藏列表失败:', err);
      });
    }
  }, [isAuthenticated, favorites.length, dispatch]);

  // 请求剪贴板权限
  useEffect(() => {
    const requestClipboardPermission = async () => {
      try {
        // 尝试请求剪贴板权限
        await navigator.clipboard.readText();
        setClipboardPermission('granted');
        console.log('剪贴板权限已获取');
      } catch (err) {
        setClipboardPermission('denied');
        console.log('需要剪贴板权限，将在用户交互时请求');
      }
    };

    // 页面加载时请求权限
    requestClipboardPermission();
  }, []);

  // 检测剪贴板内容
  useEffect(() => {
    const checkClipboard = async () => {
      try {
        const text = await navigator.clipboard.readText();
        setHasClipboardContent(text.trim().length > 0);
        setClipboardPermission('granted');
      } catch (err) {
        // 如果无法读取剪贴板（权限问题等），设为false
        setHasClipboardContent(false);
        setClipboardPermission('denied');
      }
    };

    // 只在有权限且输入框为空时检查剪贴板
    if (!text.trim() && clipboardPermission === 'granted') {
      checkClipboard();
    } else {
      setHasClipboardContent(false);
    }
  }, [text, clipboardPermission]);

  // 检测文本是否为单个单词（英文单词，不含空格和特殊字符）
  const isSingleWord = (input) => {
    // 确保文本不为空，只包含字母，长度在2-20之间
    const trimmed = input.trim();
    return /^[a-zA-Z]{2,20}$/.test(trimmed);
  };

  // 自动查询逻辑
  useEffect(() => {
    if (autoMode && text.trim()) {
      // 清除之前的定时器
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      // 检测是否为单个单词
      if (isSingleWord(text)) {
        // 如果是单词，查询单词定义
        timeoutRef.current = setTimeout(() => {
          dispatch(queryWord(text));
          dispatch(clearWordResult()); // 先清空之前的结果
        }, 1000);
      } else {
        // 如果不是单词，进行语法检查
        timeoutRef.current = setTimeout(() => {
          dispatch(clearWordResult()); // 先清空之前的结果
          dispatch(checkGrammar(text));
        }, 1000);
      }
    }
    
    // 清理函数
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [text, autoMode, dispatch]);

  // 新增：处理文本选择事件
  const handleTextSelection = (e) => {
    // 如果是右键菜单，不做处理
    if (e.type === 'contextmenu') {
      return;
    }
    
    // 获取选中的文本
    const selectedText = window.getSelection().toString().trim();
    
    // 只有当选中了有效的英文单词时才显示菜单
    if (selectedText && isSingleWord(selectedText)) {
      setContextMenu({
        show: true,
        x: e.clientX,
        y: e.clientY,
        selectedText
      });
    }
  };

  // 新增：点击页面其他区域关闭上下文菜单
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu.show) {
        setContextMenu(prev => ({ ...prev, show: false }));
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [contextMenu.show]);

  // 新增：处理查单词菜单项点击
  const handleWordLookup = () => {
    if (contextMenu.selectedText) {
      dispatch(clearWordResult());
      dispatch(queryWord(contextMenu.selectedText));
      setContextMenu(prev => ({ ...prev, show: false }));
    }
  };

  // 新增：处理粘贴查询
  const handlePasteAndCheck = async () => {
    try {
      // 先尝试请求剪贴板权限
      let clipboardText;
      try {
        clipboardText = await navigator.clipboard.readText();
      } catch (permissionErr) {
        // 如果权限被拒绝，尝试使用传统方法
        console.log('使用传统方法读取剪贴板');
        const textArea = document.createElement('textarea');
        textArea.value = '';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('paste');
        clipboardText = textArea.value;
        document.body.removeChild(textArea);
      }
      
      if (clipboardText && clipboardText.trim()) {
        setText(clipboardText);
        
        // 延迟一下让状态更新后再查询
        setTimeout(() => {
          if (isSingleWord(clipboardText)) {
            dispatch(clearWordResult());
            dispatch(queryWord(clipboardText));
          } else {
            dispatch(clearWordResult());
            dispatch(checkGrammar(clipboardText));
          }
        }, 100);
      }
    } catch (err) {
      console.error('读取剪贴板失败:', err);
      // 如果所有方法都失败，提示用户
      alert('无法读取剪贴板内容，请手动复制粘贴到输入框中');
    }
  };

  // 新增：处理收藏单词
  const handleFavorite = async () => {
    if (!isAuthenticated) {
      // 如果未登录，显示登录框
      dispatch(setShowLoginModal(true));
      return;
    }

    if (!wordResult) return;

    const word = wordResult.word;
    const isFavorited = favoriteWords.includes(word);

    try {
      if (isFavorited) {
        // 取消收藏
        const favoriteItem = favorites.find(fav => fav.word === word);
        if (favoriteItem && favoriteItem.id) {
          await dispatch(unfavoriteWord(favoriteItem.id)).unwrap();
        }
      } else {
        // 添加收藏
        await dispatch(favoriteWord({
          word: word,
          definition: wordResult.definitions?.map(item => 
            `${item.partOfSpeech}: ${item.definitions.join('; ')}`
          ).join('\n') || '',
          phonetic: wordResult.phonetic || '',
          audioUrl: wordResult.audioUrl || ''
        })).unwrap();
      }
    } catch (error) {
      console.error('收藏操作失败:', error);
      alert(error.message || '收藏操作失败，请稍后重试');
    }
  };

  const handleCheck = () => {
    if (text.trim()) {
      if (isSingleWord(text)) {
        dispatch(clearWordResult());
        dispatch(queryWord(text));
      } else {
        dispatch(checkGrammar(text));
      }
    }
  };

  // 新增：播放单词发音
  const handlePlayAudio = async () => {
    if (!wordResult || !wordResult.audioUrl) return;
    
    try {
      // 停止之前可能正在播放的音频
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      
      // 创建新的音频对象
      audioRef.current = new Audio(wordResult.audioUrl);
      setIsPlaying(true);
      
      // 播放音频
      await audioRef.current.play();
      
      // 监听播放结束事件
      audioRef.current.onended = () => {
        setIsPlaying(false);
      };
    } catch (err) {
      console.error('播放音频失败:', err);
      setIsPlaying(false);
    }
  };

  const toggleMode = () => {
    setAutoMode(!autoMode);
  };

  const handleClear = () => {
    setText('');
    dispatch(clearResult());
    setCopiedStates({
      corrected: false,
      translation: false,
      betterExpression: false
    });
  };

  const handleCopy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStates(prev => ({ ...prev, [key]: true }));
      
      // 2秒后重置复制状态
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [key]: false }));
      }, 2000);
    } catch (err) {
      console.error('复制失败:', err);
      // 降级方案：使用传统的复制方法
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedStates(prev => ({ ...prev, [key]: true }));
        setTimeout(() => {
          setCopiedStates(prev => ({ ...prev, [key]: false }));
        }, 2000);
      } catch (fallbackErr) {
        console.error('降级复制也失败:', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleSendEmail = (text, subject) => {
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(text)}`;
    window.location.href = mailtoUrl;
  };

  // 动态生成页面标题
  const getPageTitle = () => {
    if (wordResult) {
      return `${wordResult.word} - 单词查询 | LingoBuddy`;
    }
    if (result) {
      return '语法检查结果 | LingoBuddy - 英语语法检查与翻译工具';
    }
    return 'LingoBuddy - 英语语法检查与翻译工具 | 智能英文写作助手';
  };

  const getPageDescription = () => {
    if (wordResult) {
      return `"${wordResult.word}" 的英语释义、音标和用法示例。LingoBuddy 为您提供准确的英语单词查询服务。`;
    }
    return 'LingoBuddy 是一款强大的在线英语语法检查与翻译工具。支持英文语法纠错、中英互译、单词查询、更好的英文表达推荐，帮助您提升英语写作水平。';
  };

  return (
    <Container as="main">
      <Helmet>
        <title>{getPageTitle()}</title>
        <meta name="description" content={getPageDescription()} />
      </Helmet>

      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <LogoContainer>
          <LogoIcon>
            <LogoSVG />
          </LogoIcon>
          <LogoTextGroup>
            <BrandName>LingoBuddy</BrandName>
            <Tagline>语法检查 &amp; 单词查询</Tagline>
          </LogoTextGroup>
        </LogoContainer>
        <UserInfo />
      </header>
      
      <InputSection as="section" aria-label="输入区域">
        <TextArea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="请输入您想要检查语法或翻译的中文或英文文本，或输入单个英文单词查询定义..."
          aria-label="输入要检查的文本"
          role="textbox"
        />
        
        <ButtonContainer>
          <PrimaryButton 
            onClick={!text.trim() && hasClipboardContent && clipboardPermission === 'granted' ? handlePasteAndCheck : handleCheck} 
            disabled={loading || (!text.trim() && (!hasClipboardContent || clipboardPermission !== 'granted'))}
            aria-label={!text.trim() && hasClipboardContent && clipboardPermission === 'granted' ? '粘贴剪贴板内容并查询' : (isSingleWord(text) ? '查询单词定义' : '检查语法或翻译')}
          >
            {loading && <LoadingSpinner />}
            {!text.trim() && hasClipboardContent && clipboardPermission === 'granted' ? '粘贴查询' : (isSingleWord(text) ? '查询单词' : '检查语法/翻译')}
          </PrimaryButton>
          
          <SecondaryButton onClick={handleClear} aria-label="清空输入内容">
            清空内容
          </SecondaryButton>
          
          <SwitchContainer>
            <SwitchLabel as="span" id="auto-mode-label">自动查询</SwitchLabel>
            <Switch>
              <SwitchInput 
                type="checkbox" 
                checked={autoMode}
                onChange={toggleMode}
                title={autoMode ? "切换到手动查询模式" : "切换到自动查询模式"}
                aria-labelledby="auto-mode-label"
              />
              <Slider />
            </Switch>
          </SwitchContainer>
        </ButtonContainer>
        
            {error && (
          <div role="alert" style={{ color: '#ef4444', marginTop: '10px', background: 'rgba(239, 68, 68, 0.1)', padding: '8px 12px', borderRadius: '6px', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
            错误: {error}
          </div>
        )}
        
        <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)', marginTop: '5px' }}>
          {autoMode ? "自动查询模式：输入完成后1秒自动检查" : "手动查询模式：点击按钮进行检查"}
          {clipboardPermission === 'denied' && (
            <div style={{ marginTop: '5px', color: 'rgba(255, 193, 7, 0.8)' }}>
              💡 提示：点击"粘贴查询"按钮可请求剪贴板权限
            </div>
          )}
        </div>
      </InputSection>

      {/* 单词查询结果 */}
      {wordResult && !loading && (
        <WordResultSection as="section" aria-label="单词查询结果">
          <WordResultCard>
            <WordTitle as="h2">
              {wordResult.word}
              {wordResult.audioUrl && (
                <PlayButton 
                  onClick={handlePlayAudio} 
                  disabled={isPlaying}
                  aria-label={isPlaying ? '正在播放发音' : `播放 ${wordResult.word} 的发音`}
                >
                  <PlayIcon playing={isPlaying} />
                </PlayButton>
              )}
              <FavoriteButton 
                onClick={handleFavorite}
                className={favoriteWords.includes(wordResult.word) ? 'favorited' : ''}
                title={isAuthenticated ? 
                  (favoriteWords.includes(wordResult.word) ? '取消收藏' : '收藏单词') : 
                  '登录后可收藏单词'
                }
                aria-label={favoriteWords.includes(wordResult.word) ? '取消收藏此单词' : '收藏此单词'}
              >
                <FavoriteIcon favorited={favoriteWords.includes(wordResult.word)} />
              </FavoriteButton>
            </WordTitle>
            
            <PhoneticText>
              {wordResult.phonetic && wordResult.phonetic.trim() !== '' 
                ? `${wordResult.phonetic}`
                : '音标信息暂不可用'
              }
            </PhoneticText>
            
            {wordResult.definitions && wordResult.definitions.length > 0 && (
              <div>
                <DefinitionsList aria-label="单词释义列表">
                  {wordResult.definitions.map((item, index) => (
                    <React.Fragment key={index}>
                      <DefinitionItem>
                        <PartOfSpeech as="strong">{item.partOfSpeech}</PartOfSpeech>
                        {item.definitions.map((def, defIndex) => (
                          <React.Fragment key={defIndex}>
                            {defIndex > 0 && <br />} {def}
                          </React.Fragment>
                        ))}
                      </DefinitionItem>
                    </React.Fragment>
                  ))}
                </DefinitionsList>
              </div>
            )}

            {/* Skills 生成的内容 */}
            {wordResult.skillContent && (
              <SkillsContentSection>
                <SkillsTitle as="h3">📚 Skills 生成内容</SkillsTitle>
                <SkillsContent>
                  {parseSkillContent(wordResult.skillContent).map((item, index) => (
                    <SkillItem key={index}>
                      <SkillSentence>{item.sentence}</SkillSentence>
                      <SkillTranslation>{item.translation}</SkillTranslation>
                    </SkillItem>
                  ))}
                </SkillsContent>
              </SkillsContentSection>
            )}
          </WordResultCard>
        </WordResultSection>
      )}

      {/* 语法检查结果 */}
      {result && !loading && !wordResult && (
        <ResultSection as="section" aria-label="语法检查结果">
          {!isChinese && (
            <ResultCard>
              <ResultHeader>
                <ResultTitle as="h2">语法矫正</ResultTitle>
                <ButtonGroup>
                  <CopyButton 
                    onClick={() => handleCopy(result.corrected, 'corrected')}
                    className={copiedStates.corrected ? 'copied' : ''}
                    aria-label="复制语法矫正结果"
                  >
                    {copiedStates.corrected ? <CheckIcon /> : <CopyIcon />}
                    {copiedStates.corrected ? '已复制' : '复制'}
                  </CopyButton>
                  <EmailButton 
                    onClick={() => handleSendEmail(result.corrected, '')}
                    className="email"
                    aria-label="通过邮件发送语法矫正结果"
                  >
                    <EmailIcon />
                    转发到邮件
                  </EmailButton>
                </ButtonGroup>
              </ResultHeader>
              <CorrectedText onMouseUp={handleTextSelection}>{result.corrected}</CorrectedText>
            </ResultCard>
          )}

          <ResultCard>
            <ResultHeader>
              <ResultTitle as="h2">{isChinese ? '英文翻译' : '中文翻译'}</ResultTitle>
              <CopyButton 
                onClick={() => handleCopy(result.translation, 'translation')}
                className={copiedStates.translation ? 'copied' : ''}
                aria-label="复制翻译结果"
              >
                {copiedStates.translation ? <CheckIcon /> : <CopyIcon />}
                {copiedStates.translation ? '已复制' : '复制'}
              </CopyButton>
            </ResultHeader>
            <TranslationText onMouseUp={handleTextSelection}>{result.translation}</TranslationText>
          </ResultCard>

          {!isChinese && result.betterExpression && (
            <ResultCard>
              <ResultHeader>
                <ResultTitle as="h2">更好的表达</ResultTitle>
                <ButtonGroup>
                  <CopyButton 
                    onClick={() => handleCopy(result.betterExpression, 'betterExpression')}
                    className={copiedStates.betterExpression ? 'copied' : ''}
                    aria-label="复制更好的表达建议"
                  >
                    {copiedStates.betterExpression ? <CheckIcon /> : <CopyIcon />}
                    {copiedStates.betterExpression ? '已复制' : '复制'}
                  </CopyButton>
                  <EmailButton 
                    onClick={() => handleSendEmail(result.betterExpression, '')}
                    className="email"
                    aria-label="通过邮件发送更好的表达建议"
                  >
                    <EmailIcon />
                    转发到邮件
                  </EmailButton>
                </ButtonGroup>
              </ResultHeader>
              <CorrectedText onMouseUp={handleTextSelection}>{result.betterExpression}</CorrectedText>
            </ResultCard>
          )}
        </ResultSection>
      )}

      {/* 上下文菜单 */}
      {contextMenu.show && (
        <ContextMenu style={{ left: contextMenu.x, top: contextMenu.y }} role="menu" aria-label="文本操作菜单">
          <ContextMenuItem onClick={handleWordLookup} role="menuitem">
            查单词: {contextMenu.selectedText}
          </ContextMenuItem>
        </ContextMenu>
      )}
      
      {/* 登录注册Modal */}
      <LoginModal />
    </Container>
  );
};

export default GrammarChecker;